<template>
  <teleport to="body">
    <div v-if="open" class="core-transfer-backdrop" @click.self="close">
      <div
        class="core-modal-surface core-transfer-surface bg-white p-6 w-[600px] max-w-full border border-neutral-300"
        role="dialog"
        aria-modal="true"
        :aria-label="title">
        <div class="flex items-start justify-between gap-4 mb-2">
          <div class="flex items-center gap-2 text-neutral-900">
            <component
              :is="mode === 'export' ? IconFileExport : IconFileImport"
              class="h-6 w-6 shrink-0"
              stroke="1.8" />
            <h2 class="text-xl font-semibold">{{ title }}</h2>
          </div>
          <button
            type="button"
            class="core-transfer-close"
            :aria-label="t('general.button.close')"
            :title="t('general.button.close')"
            @click="close">
            <IconX class="h-5 w-5" stroke="1.8" />
          </button>
        </div>
        <p class="mb-6 text-sm text-neutral-550">{{ t('general.transfer.intro') }}</p>

        <div v-if="!hasStorage" class="core-transfer-box core-transfer-box--warning">
          {{ t('general.transfer.import.errors.storage_unavailable') }}
        </div>

        <section v-if="mode === 'export'" class="core-transfer-section">
          <p class="mb-3 text-sm text-neutral-550">{{ t('general.transfer.export.description') }}</p>
          <button
            type="button"
            class="core-action-button core-action-button--supporting h-10 px-4 inline-flex items-center gap-2 text-sm"
            :disabled="!hasStorage || exporting"
            data-testid="project-share-export"
            @click="onCreateShareFile">
            <IconFileExport size="18" stroke="2" />
            <span>{{ exporting ? t('general.transfer.export.exporting') : t('general.transfer.export.button') }}</span>
          </button>
          <p v-if="exportedFileName" class="mt-3 text-sm text-neutral-550" data-testid="project-share-export-result">
            {{ t('general.transfer.export.success', { file: exportedFileName }) }}
          </p>
          <div v-if="exportError" class="core-transfer-box core-transfer-box--error mt-3" role="alert">
            {{ exportError }}
          </div>
        </section>

        <section v-else class="core-transfer-section">
          <p class="mb-3 text-sm text-neutral-550">{{ t('general.transfer.import.description') }}</p>
          <input
            ref="fileInputRef"
            type="file"
            accept="application/json,.json"
            class="hidden"
            data-testid="project-import-input"
            @change="onFileSelected" />
          <div class="flex flex-wrap items-center gap-3">
            <button
              type="button"
              class="core-action-button core-action-button--supporting h-10 px-4 inline-flex items-center gap-2 text-sm"
              :disabled="!hasStorage || importing || resolving"
              @click="openFilePicker">
              <IconFileImport size="18" stroke="2" />
              <span>{{ t('general.transfer.import.choose_file') }}</span>
            </button>
            <span v-if="fileName" class="text-sm text-neutral-550 truncate">{{ fileName }}</span>
          </div>

          <p v-if="resolving" class="mt-4 text-sm text-neutral-550">{{ t('general.transfer.import.resolving') }}</p>

          <div v-else-if="importBlockingText" class="core-transfer-box core-transfer-box--error mt-4" role="alert">
            {{ importBlockingText }}
          </div>

          <div v-else-if="prepared?.ok && prepared.analysis" class="mt-4">
            <div class="core-transfer-box mb-3">
              <p class="font-semibold mb-1">{{ t('general.transfer.import.will_import') }}</p>
              <ul class="core-transfer-list">
                <li v-if="prepared.analysis.includesGeneral">{{ t('general.transfer.import.general_part') }}</li>
                <li v-for="role in prepared.analysis.importedRoles" :key="role">{{ roleLabel(role) }}</li>
              </ul>
              <p v-if="sourceInfo" class="mt-2 text-xs text-neutral-450">{{ sourceInfo }}</p>
            </div>

            <div
              v-if="prepared.analysis.warnings.length || prepared.compatibleUpdates.length"
              class="core-transfer-box core-transfer-box--warning mb-3">
              <p
                v-for="(issue, index) in prepared.analysis.warnings"
                :key="`warning-${index}`"
                class="core-transfer-issue">
                {{ issueText(issue) }}
              </p>
              <p v-if="prepared.compatibleUpdates.length" class="core-transfer-issue">
                {{ t('general.transfer.import.warnings.version_auto_update') }}
              </p>
            </div>

            <div class="core-transfer-box core-transfer-box--warning mb-4 font-semibold">
              {{ t('general.transfer.import.warning_overwrite') }}
            </div>

            <div v-if="importError" class="core-transfer-box core-transfer-box--error mb-4" role="alert">
              {{ importError }}
            </div>

            <div class="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                class="core-button-primary core-action-button core-action-button--primary h-11 px-6 flex-1"
                :disabled="importing"
                data-testid="project-import-confirm"
                @click="onImport">
                {{ importing ? t('general.transfer.import.importing') : t('general.transfer.import.button') }}
              </button>
              <button
                type="button"
                class="core-button-supporting core-action-button core-action-button--supporting h-11 px-6 flex-1"
                :disabled="importing"
                @click="resetImport">
                {{ t('general.button.cancel') }}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { IconFileExport, IconFileImport, IconX } from '@tabler/icons-vue'
import * as Sentry from '@sentry/vue'
import { useProjectStore } from '@/stores/project'
import { Events, useEventBus } from '@/helpers/event-bus'
import { formatTimestampWithTime } from '@/helpers/format'
import { createProjectState, resolveProjectState } from '@/services/projectStates'
import {
  buildProjectStateReference,
  buildReferenceFilename,
  parseProjectStateReference,
  type ProjectReferenceParseError,
} from '@/helpers/project-transfer'
import {
  applyPreparedProjectLoad,
  getProjectStorageContext,
  prepareProjectLoad,
  type PreparedProjectLoad,
} from '@/helpers/project-state-loader'
import { useProjectTransferTexts } from '@/helpers/project-transfer-texts'

declare const __APP_VERSION__: string

export type ProjectTransferMode = 'export' | 'import'

const props = defineProps<{ open: boolean; mode: ProjectTransferMode }>()
const emit = defineEmits<{ (e: 'close'): void }>()

const { t } = useI18n()
const title = computed(() => t(`general.transfer.${props.mode}.title`))
const router = useRouter()
const projectStore = useProjectStore()
const eventBus = useEventBus()
const { roleLabel, issueText, preparedLoadErrorText } = useProjectTransferTexts()

const fileInputRef = ref<HTMLInputElement | null>(null)
const fileName = ref('')
const parseError = ref<ProjectReferenceParseError | 'state_not_found' | 'unknown' | null>(null)
const prepared = ref<PreparedProjectLoad | null>(null)
const sourceTitle = ref<string | null>(null)
const resolving = ref(false)
const importing = ref(false)
const importError = ref<string | null>(null)
const exporting = ref(false)
const exportedFileName = ref<string | null>(null)
const exportError = ref<string | null>(null)

const hasStorage = computed(() => !!projectStore.getProject && getProjectStorageContext() !== null)

const sourceInfo = computed(() => {
  if (!prepared.value) return ''
  const parts: string[] = []
  if (sourceTitle.value) {
    parts.push(`${t('general.transfer.import.source_title')}: ${sourceTitle.value}`)
  }
  if (prepared.value.source.savedAt) {
    parts.push(`${t('general.transfer.import.saved_at')}: ${formatTimestampWithTime(prepared.value.source.savedAt)}`)
  }
  return parts.join(' · ')
})

const importBlockingText = computed(() => {
  if (parseError.value) return t(`general.transfer.import.errors.${parseError.value}`)
  if (prepared.value && !prepared.value.ok) return preparedLoadErrorText(prepared.value)
  return ''
})

function close() {
  if (importing.value || exporting.value) return
  emit('close')
}

function resetImport() {
  fileName.value = ''
  parseError.value = null
  prepared.value = null
  sourceTitle.value = null
  importError.value = null
  resolving.value = false
  if (fileInputRef.value) fileInputRef.value.value = ''
}

function resetExport() {
  exportedFileName.value = null
  exportError.value = null
}

function openFilePicker() {
  fileInputRef.value?.click()
}

function downloadJson(content: object, filename: string) {
  const blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

async function onCreateShareFile() {
  const context = getProjectStorageContext()
  const project = projectStore.getProject
  if (!context || !project || exporting.value) return

  exporting.value = true
  resetExport()
  try {
    const projectId = projectStore.ensureProjectId()
    if (!projectId) throw new Error('missing_project_id')
    const meta = (projectStore.getMeta ?? {}) as Record<string, unknown>
    const title = typeof meta.title === 'string' ? meta.title : null

    const state = await createProjectState(
      {
        projectId,
        userId: context.userId,
        source: 'export',
        meta,
        project: JSON.parse(JSON.stringify(project)) as Record<string, unknown>,
        label: title,
        appVersion: __APP_VERSION__,
      },
      context.refreshToken
    )

    const filename = buildReferenceFilename(title)
    downloadJson(buildProjectStateReference(state, __APP_VERSION__), filename)
    exportedFileName.value = filename
    Sentry.logger.info('Project share file created', { projectId, stateId: state.id })
  } catch (error) {
    Sentry.captureException(error, { tags: { operation: 'project_state_export' } })
    exportError.value = t('general.transfer.export.error')
  } finally {
    exporting.value = false
  }
}

async function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  resetImport()
  fileName.value = file.name

  let text = ''
  try {
    text = await file.text()
  } catch {
    parseError.value = 'invalid_file'
    return
  }

  const parsed = parseProjectStateReference(text)
  if (parsed.error) {
    parseError.value = parsed.error
    return
  }

  const context = getProjectStorageContext()
  if (!context) {
    parseError.value = 'unknown'
    return
  }

  resolving.value = true
  try {
    const state = await resolveProjectState(
      parsed.reference.projectId,
      parsed.reference.timestamp,
      context.refreshToken
    )
    if (!state) {
      parseError.value = 'state_not_found'
      return
    }
    sourceTitle.value = state.label ?? parsed.reference.title
    prepared.value = await prepareProjectLoad(state.project, state.meta, state.timestamp)
  } catch (error) {
    Sentry.captureException(error, { tags: { operation: 'project_state_resolve' } })
    parseError.value = 'unknown'
  } finally {
    resolving.value = false
  }
}

async function onImport() {
  if (!prepared.value?.ok || importing.value) return

  importing.value = true
  importError.value = null
  try {
    const applied = await applyPreparedProjectLoad(prepared.value)
    if (!applied) {
      importError.value = t('general.transfer.import.errors.unknown')
      return
    }

    eventBus.emit(Events.SHOW_SUCCESS, { key: 'general.transfer.import.success' })
    resetImport()
    emit('close')

    // The keyed router view has remounted the page with the loaded state by now,
    // so navigating no longer triggers the unsaved-changes prompt of the old form.
    await nextTick()
    if (projectStore.getHighestUpdateLevel) {
      await router.push({ name: 'version-update-view' })
    }
  } catch (error) {
    Sentry.captureException(error, { tags: { operation: 'project_state_import' } })
    importError.value = t('general.transfer.import.errors.unknown')
  } finally {
    importing.value = false
  }
}

watch(
  () => props.open,
  (open) => {
    if (!open) {
      resetImport()
      resetExport()
    }
  }
)
</script>

<style lang="postcss" scoped>
.core-transfer-backdrop {
  @apply fixed inset-0 overflow-y-auto overflow-x-hidden;
  @apply z-70 flex items-center justify-center p-4;
  @apply bg-neutral-100/15 backdrop-blur-[2px];
}

.core-transfer-surface {
  @apply max-h-full overflow-y-auto;
}

.core-modal-surface {
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.12),
    0 8px 16px -12px rgba(0, 0, 0, 0.2);
}

.core-transfer-close {
  @apply inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-none border-0 bg-transparent text-neutral-550;
  @apply hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-default;
}

.core-transfer-section {
  @apply border-t border-neutral-200 pt-5 mt-5;
}

.core-transfer-box {
  @apply border border-neutral-300 bg-neutral-50 px-4 py-3 text-sm text-neutral-700;
}

.core-transfer-box--warning {
  @apply border-yellow-300 bg-yellow-50 text-yellow-800;
}

.core-transfer-box--error {
  @apply border-red-300 bg-red-50 text-red-700;
}

.core-transfer-issue + .core-transfer-issue {
  @apply mt-2;
}

.core-transfer-list {
  @apply m-0 list-disc pl-5;
}
</style>
